import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Linkedin,
  Twitter,
  Instagram,
  Youtube,
  Facebook,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { MARKETS } from "../../data/site";
import { subscribeNewsletter } from "../../lib/api";

const SERVICE_LINKS = [
  { label: "AI Automation", href: "/ai-automation" },
  { label: "AI Search Optimization (GEO/AEO)", href: "/ai-search-optimization" },
  { label: "Search Engine Optimization", href: "/seo" },
  { label: "Local SEO", href: "/local-seo" },
  { label: "Paid Advertising (PPC)", href: "/paid-advertising" },
  { label: "Social Media Management", href: "/social-media" },
  { label: "Email & SMS Marketing", href: "/email-sms" },
  { label: "Conversion Rate Optimization", href: "/cro" },
  { label: "Content Marketing", href: "/content-marketing" },
  { label: "Website Design & Development", href: "/web-design" },
  { label: "Marketing Automation", href: "/marketing-automation" },
  { label: "Link Building & Digital PR", href: "/link-building" },
];

const COMPANY_LINKS = [
  { label: "About Lumotis", href: "/about" },
  { label: "Case Studies", href: "/case-studies" },
  { label: "Free SEO Audit Tool", href: "/seo-audit" },
  { label: "Free Marketing Audit", href: "/free-aso-audit" },
  { label: "Blog", href: "/blog" },
  { label: "Resources", href: "/resources" },
  { label: "Contact", href: "/contact" },
];

const TOP_MARKETS = [
  { label: "USA", code: "us" },
  { label: "United Kingdom", code: "uk" },
  { label: "Australia", code: "au" },
  { label: "UAE", code: "uae" },
  { label: "Canada", code: "ca" },
];

const TOP_INDUSTRIES = [
  { label: "Shopify & Ecommerce", slug: "shopify-ecommerce" },
  { label: "Real Estate", slug: "real-estate-marketing" },
  { label: "Salon & Spa", slug: "salon-spa-marketing" },
  { label: "Healthcare", slug: "healthcare-marketing" },
  { label: "Restaurants & Food", slug: "restaurant-marketing" },
];

const SOCIAL = [
  { Icon: Linkedin, href: "https://linkedin.com/company/lumotis", label: "LinkedIn" },
  { Icon: Twitter, href: "https://twitter.com/lumotis", label: "Twitter" },
  { Icon: Instagram, href: "https://instagram.com/lumotis", label: "Instagram" },
  { Icon: Youtube, href: "https://youtube.com/@lumotis", label: "YouTube" },
  { Icon: Facebook, href: "https://facebook.com/lumotis", label: "Facebook" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Add your email first.");
    setLoading(true);
    try {
      await subscribeNewsletter({ email, source: "footer" });
      toast.success("Subscribed. Marketing playbooks incoming.");
      setEmail("");
    } catch (err) {
      console.error(err);
      toast.error("Couldn't subscribe — try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      className="relative bg-charcoal text-white pt-20 pb-8 overflow-hidden"
      data-testid="site-footer"
    >
      <div className="absolute inset-0 bg-grid-pattern-dark opacity-40 pointer-events-none" />

      {/* Watermark */}
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 pointer-events-none select-none flex items-end overflow-hidden"
      >
        <span className="watermark text-[28vw] leading-none text-white/[0.04] -mb-12">
          Lumotis
        </span>
      </div>

      <div className="container relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 pb-14 border-b border-white/10">
          {/* Brand */}
          <div className="lg:col-span-4">
            <a
              href="/"
              className="inline-flex items-center"
              data-testid="footer-logo"
              aria-label="Lumotis — home"
            >
              <img
                src="/lumotis-logo-dark.png"
                alt="Lumotis"
                className="h-12 w-auto object-contain"
              />
            </a>
            <p className="mt-5 text-zinc-400 max-w-sm leading-relaxed">
              A full-stack digital marketing agency. AI Search, SEO, Paid Ads,
              Social Media, Email Marketing and more — all working together to
              grow your revenue.
            </p>

            <form onSubmit={subscribe} className="mt-8" data-testid="footer-newsletter">
              <span className="text-overline text-zinc-500">
                Digital marketing newsletter
              </span>
              <div className="mt-3 flex rounded-xl border border-white/15 overflow-hidden focus-within:border-electric">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourbusiness.com"
                  className="flex-1 bg-transparent px-4 py-3 text-sm placeholder:text-zinc-500 outline-none"
                  data-testid="footer-newsletter-email"
                  aria-label="Email for newsletter"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="footer-newsletter-submit"
                  aria-label="Subscribe to newsletter"
                  className="bg-electric text-white px-4 hover:bg-electric-400 transition-colors disabled:opacity-60"
                >
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </form>
          </div>

          {/* Services */}
          <div className="lg:col-span-2">
            <p className="text-overline text-zinc-500 mb-5">Services</p>
            <ul className="space-y-3">
              {SERVICE_LINKS.map((l) => (
                <li key={l.href}>
                  <Link
                    to={l.href}
                    className="text-sm text-zinc-300 hover:text-electric transition-colors"
                    data-testid={`footer-service-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Markets — Top 5 + View all */}
          <div className="lg:col-span-2">
            <p className="text-overline text-zinc-500 mb-5">Markets</p>
            <ul className="space-y-3">
              {TOP_MARKETS.map((m) => (
                <li key={m.code}>
                  <Link
                    to={`/${m.code}`}
                    className="text-sm text-zinc-300 hover:text-electric transition-colors"
                    data-testid={`footer-market-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {m.label} ASO
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/us/locations"
                  className="text-sm text-zinc-300 hover:text-electric transition-colors"
                  data-testid="footer-us-locations"
                >
                  USA cities & states
                </Link>
              </li>
              <li>
                <Link
                  to="/markets"
                  className="inline-flex items-center gap-1 text-sm text-electric font-semibold hover:gap-2 transition-all"
                  data-testid="footer-market-view-all"
                >
                  View all markets <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </li>
            </ul>
          </div>

          {/* Industries — Top 5 */}
          <div className="lg:col-span-2">
            <p className="text-overline text-zinc-500 mb-5">Industries</p>
            <ul className="space-y-3">
              {TOP_INDUSTRIES.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/${c.slug}`}
                    className="text-sm text-zinc-300 hover:text-electric transition-colors"
                    data-testid={`footer-industry-${c.slug}`}
                  >
                    {c.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div className="lg:col-span-2">
            <p className="text-overline text-zinc-500 mb-5">Company</p>
            <ul className="space-y-3">
              {COMPANY_LINKS.map((l) => (
                <li key={l.href}>
                  <Link
                    to={l.href}
                    className="text-sm text-zinc-300 hover:text-electric transition-colors"
                    data-testid={`footer-company-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-8">
              <p className="text-overline text-zinc-500 mb-4">Follow</p>
              <div className="flex items-center gap-2">
                {SOCIAL.map(({ Icon, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    data-testid={`footer-social-${label.toLowerCase()}`}
                    className="w-9 h-9 grid place-items-center rounded-xl border border-white/15 hover:bg-electric hover:border-electric transition-colors"
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row md:items-center justify-between gap-4 text-xs text-zinc-500">
          <p>
            © {new Date().getFullYear()} Lumotis Digital Media · Full-Stack Digital Marketing Agency
          </p>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="hover:text-electric" data-testid="footer-privacy">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-electric" data-testid="footer-terms">
              Terms
            </Link>
            <Link to="/cookies" className="hover:text-electric" data-testid="footer-cookies">
              Cookies
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
